/**
 * D-047 — indicadores de eliminação da transmissão vertical de sífilis e HIV (planilha "Indicadores de
 * impacto e processo"), calculados com os registros do MetaEpi, por município de residência e ano.
 * As metas vêm do banco (`indicadores_metas`, versionadas); aqui ficam só as fórmulas e o selo.
 */
import { z } from 'zod';

export const CODIGOS_INDICADOR = [
  'incidencia_sifilis_congenita',
  'cobertura_teste_sifilis',
  'cobertura_teste_hiv',
  'tratamento_adequado_sifilis',
  'quatro_consultas',
  'tarv_gestante_hiv',
  'incidencia_hiv_tv',
  'taxa_tv_hiv',
] as const;
export type CodigoIndicador = (typeof CODIGOS_INDICADOR)[number];

const metaSchema = z.object({
  sentido: z.enum(['menor', 'maior']),
  escala: z.union([z.literal(100), z.literal(1000)]),
  eliminacao: z.number(),
  ouro: z.number(),
  prata: z.number(),
  bronze: z.number(),
});
export type MetaIndicador = z.infer<typeof metaSchema>;

export const metasSchema = z.object(
  Object.fromEntries(CODIGOS_INDICADOR.map((c) => [c, metaSchema])) as Record<
    CodigoIndicador,
    typeof metaSchema
  >,
);
export type MetasIndicadores = z.infer<typeof metasSchema>;

export interface DescricaoIndicador {
  titulo: string;
  tipo: 'impacto' | 'processo';
  numerador: string;
  denominador: string;
  /** Indicadores que dependem de dado ainda não registrado no sistema. */
  emDesenvolvimento?: string;
  observacao?: string;
}

export const DESCRICAO_INDICADOR: Record<CodigoIndicador, DescricaoIndicador> = {
  incidencia_sifilis_congenita: {
    titulo: 'Incidência de sífilis congênita (por 1.000 nascidos vivos)',
    tipo: 'impacto',
    numerador:
      'Casos de sífilis congênita notificados no MetaEpi, por ano de diagnóstico e município de residência da mãe',
    denominador: 'Nascidos vivos de mães residentes no município (SINASC), informados pelo gestor',
  },
  cobertura_teste_sifilis: {
    titulo: 'Gestantes com pelo menos um teste de sífilis no pré-natal',
    tipo: 'processo',
    numerador: 'Gestantes com teste de sífilis registrado no pré-natal',
    denominador: 'Gestantes que iniciaram o pré-natal no ano',
  },
  cobertura_teste_hiv: {
    titulo: 'Gestantes com pelo menos um teste de HIV no pré-natal',
    tipo: 'processo',
    numerador: 'Gestantes com teste de HIV registrado no pré-natal',
    denominador: 'Gestantes que iniciaram o pré-natal no ano',
  },
  tratamento_adequado_sifilis: {
    titulo: 'Gestantes com sífilis tratadas adequadamente',
    tipo: 'processo',
    numerador:
      'Gestantes com tratamento adequado com penicilina (D-004), avaliado na data do parto',
    denominador: 'Gestantes com sífilis diagnosticada no ano e parto registrado',
    observacao: 'Gestantes com sífilis ainda sem parto aparecem à parte, como "em acompanhamento".',
  },
  quatro_consultas: {
    titulo: 'Nascidos vivos de mães com 4 ou mais consultas de pré-natal',
    tipo: 'processo',
    numerador: 'Nascidos vivos cujas mães tiveram 4 ou mais consultas de pré-natal registradas',
    denominador: 'Nascidos vivos registrados no MetaEpi no ano',
    observacao: 'O indicador oficial usa o SINASC; aqui, os partos registrados no MetaEpi.',
  },
  tarv_gestante_hiv: {
    titulo: 'Gestantes com HIV em terapia antirretroviral (TARV)',
    tipo: 'processo',
    numerador: 'Gestantes com HIV e TARV registrada',
    denominador: 'Gestantes com HIV diagnosticado no ano',
  },
  incidencia_hiv_tv: {
    titulo: 'Incidência de HIV por transmissão vertical (por 1.000 nascidos vivos)',
    tipo: 'impacto',
    numerador: 'Crianças infectadas pelo HIV por transmissão vertical',
    denominador: 'Nascidos vivos (SINASC)',
    emDesenvolvimento: 'Depende do seguimento da criança exposta ao HIV, ainda não disponível.',
  },
  taxa_tv_hiv: {
    titulo: 'Taxa de transmissão vertical do HIV',
    tipo: 'impacto',
    numerador: 'Crianças infectadas pelo HIV por transmissão vertical',
    denominador: 'Gestantes com HIV, por ano do parto',
    emDesenvolvimento: 'Depende do seguimento da criança exposta ao HIV, ainda não disponível.',
  },
};

export const SELOS = [
  'eliminacao',
  'ouro',
  'prata',
  'bronze',
  'abaixo',
  'sem_dados',
  'em_desenvolvimento',
] as const;
export type Selo = (typeof SELOS)[number];
export const ROTULO_SELO: Record<Selo, string> = {
  eliminacao: 'Meta de eliminação',
  ouro: 'Selo Ouro',
  prata: 'Selo Prata',
  bronze: 'Selo Bronze',
  abaixo: 'Abaixo da meta',
  sem_dados: 'Sem dados',
  em_desenvolvimento: 'Em desenvolvimento',
};

export interface ResultadoIndicador {
  codigo: CodigoIndicador;
  numerador: number | null;
  denominador: number | null;
  /** Valor na escala do indicador (% ou por 1.000), arredondado a 1 casa; null sem denominador. */
  valor: number | null;
  selo: Selo;
  /** Por que não há valor (ex.: falta informar os nascidos vivos). */
  motivo?: string;
  /** Contagem complementar (ex.: gestantes com sífilis ainda sem parto). */
  emAcompanhamento?: number;
}

/** O melhor selo alcançado pelo valor (D-047). */
export function seloDe(
  valor: number,
  meta: MetaIndicador,
): Exclude<Selo, 'sem_dados' | 'em_desenvolvimento'> {
  const alcanca = (limite: number) =>
    meta.sentido === 'menor' ? valor <= limite : valor >= limite;
  if (alcanca(meta.eliminacao)) return 'eliminacao';
  if (alcanca(meta.ouro)) return 'ouro';
  if (alcanca(meta.prata)) return 'prata';
  if (alcanca(meta.bronze)) return 'bronze';
  return 'abaixo';
}

export function resultado(
  codigo: CodigoIndicador,
  metas: MetasIndicadores,
  numerador: number,
  denominador: number | null,
  extra: { motivo?: string; emAcompanhamento?: number } = {},
): ResultadoIndicador {
  const meta = metas[codigo];
  if (!denominador) {
    return {
      codigo,
      numerador,
      denominador,
      valor: null,
      selo: 'sem_dados',
      motivo: extra.motivo ?? 'Sem registros no período.',
      emAcompanhamento: extra.emAcompanhamento,
    };
  }
  const valor = Math.round((numerador / denominador) * meta.escala * 10) / 10;
  return {
    codigo,
    numerador,
    denominador,
    valor,
    selo: seloDe(valor, meta),
    emAcompanhamento: extra.emAcompanhamento,
  };
}

/** Uma gestação, resumida para os indicadores (montada pela API a partir dos registros). */
export interface GestacaoIndicador {
  /** Ano em que o pré-natal começou (1ª consulta ou 1º exame do pré-natal); null sem pré-natal. */
  anoPrenatal: number | null;
  testeSifilisPrenatal: boolean;
  testeHivPrenatal: boolean;
  /** Sífilis na gestação: ano do 1º teste reagente e adequação no parto (null = ainda sem parto). */
  sifilis: { ano: number; adequado: boolean | null } | null;
  /** HIV na gestação: ano do 1º teste reagente e se há TARV registrada. */
  hiv: { ano: number; tarv: boolean } | null;
  /** Parto registrado: ano e quantos nascidos vivos. */
  parto: { ano: number; nascidosVivos: number } | null;
  consultasPrenatal: number;
}

export interface EntradaIndicadores {
  ano: number;
  gestacoes: GestacaoIndicador[];
  /** Casos de sífilis congênita notificados (não cancelados) no ano, de mães residentes. */
  casosSifilisCongenita: number;
  /** Nascidos vivos do SINASC informados para o município e o ano (D-048). */
  nascidosVivosSinasc: number | null;
}

export function calcularIndicadores(
  metas: MetasIndicadores,
  e: EntradaIndicadores,
): ResultadoIndicador[] {
  const conta = <T>(lista: T[], filtro: (x: T) => boolean) => lista.filter(filtro).length;
  const comPrenatal = e.gestacoes.filter((g) => g.anoPrenatal === e.ano);
  const comSifilis = e.gestacoes.filter((g) => g.sifilis?.ano === e.ano);
  const sifilisComParto = comSifilis.filter((g) => g.sifilis?.adequado !== null);
  const comHiv = e.gestacoes.filter((g) => g.hiv?.ano === e.ano);
  const partos = e.gestacoes.filter((g) => g.parto?.ano === e.ano);
  const soma = (lista: GestacaoIndicador[]) =>
    lista.reduce((t, g) => t + (g.parto?.nascidosVivos ?? 0), 0);

  return [
    resultado(
      'incidencia_sifilis_congenita',
      metas,
      e.casosSifilisCongenita,
      e.nascidosVivosSinasc,
      e.nascidosVivosSinasc === null
        ? { motivo: 'Falta informar os nascidos vivos do SINASC deste município e ano.' }
        : {},
    ),
    resultado(
      'cobertura_teste_sifilis',
      metas,
      conta(comPrenatal, (g) => g.testeSifilisPrenatal),
      comPrenatal.length,
    ),
    resultado(
      'cobertura_teste_hiv',
      metas,
      conta(comPrenatal, (g) => g.testeHivPrenatal),
      comPrenatal.length,
    ),
    resultado(
      'tratamento_adequado_sifilis',
      metas,
      conta(sifilisComParto, (g) => g.sifilis?.adequado === true),
      sifilisComParto.length,
      { emAcompanhamento: comSifilis.length - sifilisComParto.length },
    ),
    resultado(
      'quatro_consultas',
      metas,
      soma(partos.filter((g) => g.consultasPrenatal >= 4)),
      soma(partos),
    ),
    resultado(
      'tarv_gestante_hiv',
      metas,
      conta(comHiv, (g) => g.hiv?.tarv === true),
      comHiv.length,
    ),
    ...(['incidencia_hiv_tv', 'taxa_tv_hiv'] as const).map((codigo): ResultadoIndicador => ({
      codigo,
      numerador: null,
      denominador: null,
      valor: null,
      selo: 'em_desenvolvimento',
      motivo: DESCRICAO_INDICADOR[codigo].emDesenvolvimento,
    })),
  ];
}

/**
 * D-049 — perfis agregados (gestor regional e visualizador) não veem contagens de 1 a 4, para não
 * identificar uma pessoa num município pequeno. A taxa continua visível.
 */
export function ocultarContagensPequenas(r: ResultadoIndicador): ResultadoIndicador & {
  numeradorExibido: string | null;
  denominadorExibido: string | null;
} {
  const exibir = (n: number | null) => (n === null ? null : n >= 1 && n <= 4 ? '< 5' : String(n));
  return { ...r, numeradorExibido: exibir(r.numerador), denominadorExibido: exibir(r.denominador) };
}

// ---------------------------------------------------------------- nascidos vivos (D-048)
const anoValido = z
  .number({ message: 'Informe o ano.' })
  .int()
  .min(2000, { message: 'Ano a partir de 2000.' })
  .max(2100, { message: 'Ano inválido.' });

export const nascidosVivosSchema = z.object({
  municipioIbge: z.string().regex(/^\d{7}$/, { message: 'Código IBGE com 7 dígitos.' }),
  ano: anoValido,
  nascidosVivos: z
    .number({ message: 'Informe o número de nascidos vivos.' })
    .int({ message: 'Use um número inteiro.' })
    .min(0, { message: 'O número não pode ser negativo.' })
    .max(1_000_000),
});
export type DadosNascidosVivos = z.infer<typeof nascidosVivosSchema>;

export const planilhaNascidosVivosSchema = z.object({
  linhas: z.array(nascidosVivosSchema).min(1, { message: 'A planilha não tem linhas.' }).max(500),
});

/**
 * Lê a planilha `codigo_ibge;ano;nascidos_vivos` (aceita vírgula ou ponto e vírgula, cabeçalho e
 * código IBGE de 6 dígitos, como no TabNet). Devolve as linhas válidas e os erros por linha.
 */
export function lerPlanilhaNascidosVivos(
  texto: string,
  completarIbge: (codigo6: string) => string | undefined,
): { linhas: DadosNascidosVivos[]; erros: string[] } {
  const linhas: DadosNascidosVivos[] = [];
  const erros: string[] = [];
  // Sem a marca de UTF-8 (BOM) que o Excel põe no início
  (texto.charCodeAt(0) === 0xfeff ? texto.slice(1) : texto).split(/\r?\n/).forEach((bruta, i) => {
    const linha = bruta.trim();
    if (!linha) return;
    const [codigo = '', ano = '', nv = ''] = linha
      .split(/[;,\t]/)
      .map((c) => c.trim().replace(/^"|"$/g, ''));
    // Cabeçalho
    if (i === 0 && !/^\d/.test(codigo)) return;
    const ibge = /^\d{6}$/.test(codigo) ? completarIbge(codigo) : codigo;
    const r = nascidosVivosSchema.safeParse({
      municipioIbge: ibge ?? codigo,
      ano: Number(ano),
      nascidosVivos: Number(nv.replace(/\./g, '')),
    });
    if (r.success) linhas.push(r.data);
    else erros.push(`Linha ${i + 1}: ${r.error.issues[0]?.message ?? 'inválida'}`);
  });
  return { linhas, erros };
}
